/**
 * Copyright Highway9 Networks Inc.
 *
 * Task-tool child sessions (delegated primary/subagents) are embedded under the
 * parent chat session and hidden from the session switcher.
 */

import type { AiAssistantMessage, AiAssistantMessagePart, AiAssistantSession } from "~/services/ai-assistant-service";

export type TaskDelegationInfo = {
  childSessionId: string;
  taskPartId?: string;
  agentName?: string;
  title?: string;
};

export function sessionParentId(session: AiAssistantSession | Record<string, unknown>): string | undefined {
  const s = session as Record<string, unknown>;
  const raw = s.parentId ?? s.parentID ?? s.parent_id;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  const parent = s.parent;
  if (parent && typeof parent === "object" && typeof (parent as { id?: unknown }).id === "string") {
    return (parent as { id: string }).id;
  }
  return undefined;
}

/** Child session id from a running/completed Task tool part (`state.metadata.sessionId`). */
export function taskChildSessionIdFromPart(part: AiAssistantMessagePart): string | undefined {
  const p = part as Record<string, unknown>;
  const tool = p.tool ?? p.name;
  if (p.type !== "tool" && p.type !== "tool-call" && p.type !== "tool-invocation") return undefined;
  if (tool !== "task") return undefined;

  const state = (p.state ?? {}) as Record<string, unknown>;
  const meta = (state.metadata ?? p.metadata ?? {}) as Record<string, unknown>;
  const sid = meta.sessionId ?? meta.session_id ?? meta.sessionID;
  return typeof sid === "string" && sid.trim().length > 0 ? sid.trim() : undefined;
}

export function taskDelegationInfoFromPart(part: AiAssistantMessagePart): TaskDelegationInfo | null {
  const childSessionId = taskChildSessionIdFromPart(part);
  if (!childSessionId) return null;
  const p = part as Record<string, unknown>;
  const state = (p.state ?? {}) as Record<string, unknown>;
  const input = (state.input ?? {}) as Record<string, unknown>;
  const partId = p.id;
  return {
    childSessionId,
    taskPartId: typeof partId === "string" ? partId : undefined,
    agentName:
      typeof input.subagent_type === "string"
        ? input.subagent_type
        : typeof input.subagentType === "string"
          ? input.subagentType
          : undefined,
    title:
      typeof state.title === "string"
        ? state.title
        : typeof input.description === "string"
          ? input.description
          : undefined,
  };
}

/** Collect task delegations from committed + in-flight parts (deduped by child session id). */
export function collectTaskDelegations(
  message: AiAssistantMessage,
  streamingParts?: AiAssistantMessagePart[]
): TaskDelegationInfo[] {
  const parts = streamingParts?.length ? streamingParts : message.parts ?? [];
  const out: TaskDelegationInfo[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const info = taskDelegationInfoFromPart(part);
    if (!info || seen.has(info.childSessionId)) continue;
    seen.add(info.childSessionId);
    out.push(info);
  }
  return out;
}

function turnStartIndex(messages: AiAssistantMessage[], lastAssistantIndex: number): number {
  for (let i = lastAssistantIndex; i >= 0; i--) {
    if ((messages[i].info.role ?? "assistant") === "user") {
      return i + 1;
    }
    if (i === 0) return 0;
  }
  return 0;
}

function lastUserIndex(messages: AiAssistantMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if ((messages[i].info.role ?? "assistant") === "user") return i;
  }
  return -1;
}

function addEmbeddedChildId(
  ids: Set<string>,
  childSessionId: string,
  parentSessionId: string,
  embeddedByChildId: Record<string, { parentSessionId: string }>
): void {
  if (embeddedByChildId[childSessionId]?.parentSessionId !== parentSessionId) return;
  ids.add(childSessionId);
}

/** Embedded child session ids delegated during a completed user turn. */
export function embeddedChildIdsForTurn(
  messages: AiAssistantMessage[],
  lastAssistantIndex: number,
  streamingPartsById: Record<string, AiAssistantMessagePart[]>,
  parentSessionId: string,
  embeddedByChildId: Record<string, { parentSessionId: string }>
): Set<string> {
  const ids = new Set<string>();
  const start = turnStartIndex(messages, lastAssistantIndex);
  for (let i = start; i <= lastAssistantIndex; i++) {
    const msg = messages[i];
    const id = msg.info.id;
    const stream = id && streamingPartsById[id]?.length ? streamingPartsById[id] : undefined;
    for (const d of collectTaskDelegations(msg, stream)) {
      addEmbeddedChildId(ids, d.childSessionId, parentSessionId, embeddedByChildId);
    }
  }
  return ids;
}

/** Embedded child session ids for the in-flight turn (committed + streaming tail). */
export function embeddedChildIdsForActiveTurn(
  messages: AiAssistantMessage[],
  streamingPartsById: Record<string, AiAssistantMessagePart[]>,
  tailStreamingParts: AiAssistantMessagePart[],
  parentSessionId: string,
  embeddedByChildId: Record<string, { parentSessionId: string }>
): Set<string> {
  const ids = new Set<string>();
  const start = lastUserIndex(messages) + 1;
  for (let i = start; i < messages.length; i++) {
    const msg = messages[i];
    const id = msg.info.id;
    const stream = id && streamingPartsById[id]?.length ? streamingPartsById[id] : undefined;
    for (const d of collectTaskDelegations(msg, stream)) {
      addEmbeddedChildId(ids, d.childSessionId, parentSessionId, embeddedByChildId);
    }
  }
  for (const d of collectTaskDelegations({ info: { role: "assistant" }, parts: [] }, tailStreamingParts)) {
    addEmbeddedChildId(ids, d.childSessionId, parentSessionId, embeddedByChildId);
  }
  const messageIds = new Set(
    messages
      .map((m) => m.info.id)
      .filter((id): id is string => typeof id === "string" && id.length > 0)
  );
  for (const [streamId, parts] of Object.entries(streamingPartsById)) {
    if (messageIds.has(streamId) || !parts?.length) continue;
    for (const d of collectTaskDelegations(
      { info: { id: streamId, role: "assistant" }, parts: [] },
      parts
    )) {
      addEmbeddedChildId(ids, d.childSessionId, parentSessionId, embeddedByChildId);
    }
  }
  return ids;
}
